import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

const DisplaySelectedPopup = ({ onClose, onSelect }) => {
  const [showAddSize, setShowAddSize] = useState(false);
  const [selectedDisplay, setSelectedDisplay] = useState(null);
  const [showResolutionDropdown, setShowResolutionDropdown] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState("");
  const [isCustomResolution, setIsCustomResolution] = useState(false);
  const [customResolutionInput, setCustomResolutionInput] = useState("");
  const customInputRef = useRef(null);
  const [showInchDropdown, setShowInchDropdown] = useState(false);
  const [selectedInch, setSelectedInch] = useState("");
  const [isCustomInch, setIsCustomInch] = useState(false);
  const [customInchInput, setCustomInchInput] = useState("");
  const customInchInputRef = useRef(null);
  const [displayName, setDisplayName] = useState("");
  const [calculatedSize, setCalculatedSize] = useState({
    width: "",
    height: "",
  });
  const [editingDisplay, setEditingDisplay] = useState(null);

  const [displayOptions, setDisplayOptions] = useState([
    {
      id: "large",
      title: "대형 세로형 사이니지",
      specs: "55인치 / 1080x1920(FHD) / 1217×685mm",
    },
    {
      id: "medium",
      title: "중형 세로형 사이니지",
      specs: "43인치 / 1080x1920(FHD) / 570x970mm",
    },
    {
      id: "small",
      title: "소형 세로형 키오스크",
      specs: "27인치 / 1080x1920(FHD) / 355x610mm",
    },
  ]);

  const resolutionOptions = [
    { id: "hd", label: "HD | 1280 x 720" },
    { id: "fhd", label: "FHD | 1920 x 1080" },
    { id: "qhd", label: "QHD (2K) | 2560 x 1440" },
    { id: "uhd", label: "UHD (4K) | 3840 x 2160" },
    { id: "custom", label: "기타 (입력해주세요)" },
  ];

  const inchOptions = [
    { id: "21.5", label: "21.5 인치" },
    { id: "27", label: "27 인치" },
    { id: "32", label: "32 인치" },
    { id: "43", label: "43 인치" },
    { id: "55", label: "55 인치" },
    { id: "65", label: "65 인치" },
    { id: "custom", label: "기타 (입력해주세요)" },
  ];

  const handleDisplaySelect = (displayId) => {
    setSelectedDisplay(displayId);
  };

  const handleComplete = () => {
    if (selectedDisplay && onSelect) {
      onSelect(selectedDisplay);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleAddSizeClick = () => {
    setShowAddSize(true);
  };

  const handleBackToSelection = () => {
    setShowAddSize(false);
    setEditingDisplay(null);
    setDisplayName("");
    setSelectedResolution("");
    setSelectedInch("");
    setIsCustomResolution(false);
    setIsCustomInch(false);
    setCustomResolutionInput("");
    setCustomInchInput("");
    setCalculatedSize({ width: "", height: "" });
  };

  const handleSaveNewDisplay = () => {
    // 입력값 검증
    if (!displayName || !selectedResolution || !selectedInch) {
      alert("모든 필드를 입력해주세요.");
      return;
    }

    // 새 디스플레이 객체 생성
    const newDisplay = {
      id: `custom_${Date.now()}`,
      title: displayName,
      specs: `${selectedInch} / ${selectedResolution} / ${calculatedSize.width}×${calculatedSize.height}mm`,
    };

    // 디스플레이 목록에 추가
    setDisplayOptions((prev) => [...prev, newDisplay]);

    // 폼 초기화
    setDisplayName("");
    setSelectedResolution("");
    setSelectedInch("");
    setIsCustomResolution(false);
    setIsCustomInch(false);
    setCustomResolutionInput("");
    setCustomInchInput("");
    setCalculatedSize({ width: "", height: "" });

    // 추가 화면 닫기
    setShowAddSize(false);
  };

  const handleEditDisplay = (display) => {
    setEditingDisplay(display);

    // 기존 정보로 폼 채우기
    setDisplayName(display.title);

    // specs에서 정보 파싱
    const specs = display.specs.split(" / ");
    if (specs.length >= 3) {
      const inch = specs[0];
      const resolution = specs[1];

      setSelectedInch(inch);
      setSelectedResolution(resolution);

      // 사이즈 계산
      const size = calculateSizeFromInch(inch);
      setCalculatedSize(size);
    }

    setShowAddSize(true);
  };

  const handleDeleteDisplay = () => {
    if (
      editingDisplay &&
      window.confirm("정말로 이 디스플레이를 삭제하시겠습니까?")
    ) {
      setDisplayOptions((prev) =>
        prev.filter((display) => display.id !== editingDisplay.id)
      );
      handleBackToSelection();
    }
  };

  const handleEditSave = () => {
    // 입력값 검증
    if (!displayName || !selectedResolution || !selectedInch) {
      alert("모든 필드를 입력해주세요.");
      return;
    }

    if (editingDisplay) {
      // 편집 모드: 기존 디스플레이 업데이트
      const updatedDisplay = {
        ...editingDisplay,
        title: displayName,
        specs: `${selectedInch} / ${selectedResolution} / ${calculatedSize.width}×${calculatedSize.height}mm`,
      };

      setDisplayOptions((prev) =>
        prev.map((display) =>
          display.id === editingDisplay.id ? updatedDisplay : display
        )
      );
    } else {
      // 새 디스플레이 추가 모드
      const newDisplay = {
        id: `custom_${Date.now()}`,
        title: displayName,
        specs: `${selectedInch} / ${selectedResolution} / ${calculatedSize.width}×${calculatedSize.height}mm`,
      };

      setDisplayOptions((prev) => [...prev, newDisplay]);
    }

    // 폼 초기화
    handleBackToSelection();
  };

  const handleResolutionToggle = () => {
    setShowResolutionDropdown(!showResolutionDropdown);
  };

  const handleResolutionSelect = (resolution) => {
    if (resolution.id === "custom") {
      setIsCustomResolution(true);
      setSelectedResolution("");
      setCustomResolutionInput("");
    } else {
      setIsCustomResolution(false);
      setSelectedResolution(resolution.label);
      setCustomResolutionInput("");
    }
    setShowResolutionDropdown(false);
  };

  const handleCustomResolutionChange = (e) => {
    const value = e.target.value;
    setCustomResolutionInput(value);
    setSelectedResolution(value);
  };

  useEffect(() => {
    if (isCustomResolution && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [isCustomResolution]);

  const handleInchToggle = () => {
    setShowInchDropdown(!showInchDropdown);
  };

  const handleInchSelect = (inch) => {
    if (inch.id === "custom") {
      setIsCustomInch(true);
      setSelectedInch("");
      setCustomInchInput("");
    } else {
      setIsCustomInch(false);
      setSelectedInch(inch.label);
      setCustomInchInput("");
    }
    setShowInchDropdown(false);
  };

  const handleCustomInchChange = (e) => {
    const value = e.target.value;
    setCustomInchInput(value);
    setSelectedInch(value);
  };

  useEffect(() => {
    if (isCustomInch && customInchInputRef.current) {
      customInchInputRef.current.focus();
    }
  }, [isCustomInch]);

  // 인치에 따른 사이즈 계산 함수
  const calculateSizeFromInch = (inch) => {
    if (!inch) return { width: "", height: "" };

    const inchValue = parseFloat(inch.replace(" 인치", "").replace("인치", ""));
    if (isNaN(inchValue)) return { width: "", height: "" };

    // 16:9 비율로 계산 (세로형이므로 9:16)
    const diagonalMM = inchValue * 25.4; // 인치를 mm로 변환
    const aspectRatio = 9 / 16; // 세로형 비율

    const height = Math.round(
      diagonalMM / Math.sqrt(1 + aspectRatio * aspectRatio)
    );
    const width = Math.round(height * aspectRatio);

    return { width: width.toString(), height: height.toString() };
  };

  // 인치 변경 시 사이즈 자동 계산
  useEffect(() => {
    if (selectedInch) {
      const size = calculateSizeFromInch(selectedInch);
      setCalculatedSize(size);
    }
  }, [selectedInch]);

  if (showAddSize) {
    return (
      <PopupContainer>
        <PopupContent>
          <Header>
            <Title>디스플레이를 선택해주세요</Title>
            <CloseButton onClick={onClose}>
              <CloseButtonIcon />
            </CloseButton>
          </Header>

          <Content isAddSizeMode={true}>
            {/* 기존 디스플레이 목록 */}
            <DisplayList>
              {displayOptions.map((display) => (
                <DisplayOption
                  key={display.id}
                  selected={selectedDisplay === display.id}
                >
                  <RadioButton onClick={() => handleDisplaySelect(display.id)}>
                    <RadioButtonInner
                      selected={selectedDisplay === display.id}
                    />
                  </RadioButton>
                  <DisplayInfo>
                    <DisplayTitle>{display.title}</DisplayTitle>
                    <DisplaySpecs>{display.specs}</DisplaySpecs>
                  </DisplayInfo>
                  <EditIconButton onClick={() => handleEditDisplay(display)}>
                    <EditIcon />
                  </EditIconButton>
                </DisplayOption>
              ))}

              {/* 새 디스플레이 추가 섹션 */}
              <AddDisplaySection>
                <InputGroup>
                  <Label>디스플레이명</Label>
                  <InputBox>
                    <InputText
                      placeholder="입력해주세요"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                    />
                  </InputBox>
                </InputGroup>

                <InputRow>
                  <InputGroup>
                    <Label>해상도</Label>
                    <ResolutionSelectContainer>
                      {isCustomResolution ? (
                        <CustomResolutionInput
                          ref={customInputRef}
                          placeholder="해상도를 입력해주세요 (예: 1920 x 1080)"
                          value={customResolutionInput}
                          onChange={handleCustomResolutionChange}
                        />
                      ) : (
                        <SelectBox onClick={handleResolutionToggle}>
                          <SelectText>
                            {selectedResolution || "선택하세요"}
                          </SelectText>
                          <ArrowIcon isOpen={showResolutionDropdown} />
                        </SelectBox>
                      )}
                      {showResolutionDropdown && (
                        <ResolutionDropdown>
                          {resolutionOptions.map((resolution) => (
                            <ResolutionOption
                              key={resolution.id}
                              onClick={() => handleResolutionSelect(resolution)}
                            >
                              <ResolutionCheckbox />
                              <ResolutionLabel>
                                {resolution.label}
                              </ResolutionLabel>
                            </ResolutionOption>
                          ))}
                        </ResolutionDropdown>
                      )}
                    </ResolutionSelectContainer>
                  </InputGroup>

                  <InputGroup>
                    <Label>인치</Label>
                    <InchSelectContainer>
                      {isCustomInch ? (
                        <CustomInchInput
                          ref={customInchInputRef}
                          placeholder="인치를 입력해주세요 (예: 24)"
                          value={customInchInput}
                          onChange={handleCustomInchChange}
                        />
                      ) : (
                        <SelectBox onClick={handleInchToggle}>
                          <SelectText>
                            {selectedInch || "선택하세요"}
                          </SelectText>
                          <ArrowIcon isOpen={showInchDropdown} />
                        </SelectBox>
                      )}
                      {showInchDropdown && (
                        <InchDropdown>
                          {inchOptions.map((inch) => (
                            <InchOption
                              key={inch.id}
                              onClick={() => handleInchSelect(inch)}
                            >
                              <InchCheckbox />
                              <InchLabel>{inch.label}</InchLabel>
                            </InchOption>
                          ))}
                        </InchDropdown>
                      )}
                    </InchSelectContainer>
                  </InputGroup>
                </InputRow>

                <InputGroup>
                  <Label>사이즈 확인</Label>
                  <SizeInputRow>
                    <SizeBox>
                      <SizeText>
                        {calculatedSize.width || "가로 사이즈"}
                      </SizeText>
                      <Unit>mm</Unit>
                    </SizeBox>
                    <SizeBox>
                      <SizeText>
                        {calculatedSize.height || "세로 사이즈"}
                      </SizeText>
                      <Unit>mm</Unit>
                    </SizeBox>
                  </SizeInputRow>
                </InputGroup>

                <ButtonRow hasDelete={!!editingDisplay}>
                  {editingDisplay && (
                    <DeleteButton onClick={handleDeleteDisplay}>
                      <TrashIcon />
                      디스플레이 삭제하기
                    </DeleteButton>
                  )}
                  <RightButtonGroup>
                    <CancelButton onClick={handleBackToSelection}>
                      {editingDisplay ? "편집 취소" : "취소"}
                    </CancelButton>
                    <SaveButton onClick={handleEditSave}>
                      {editingDisplay ? "편집 저장" : "새 디스플레이 저장"}
                    </SaveButton>
                  </RightButtonGroup>
                </ButtonRow>
              </AddDisplaySection>
            </DisplayList>
          </Content>
        </PopupContent>
      </PopupContainer>
    );
  }

  return (
    <PopupContainer>
      <PopupContent>
        <Header>
          <Title>디스플레이를 선택해주세요</Title>
          <CloseButton onClick={onClose}>
            <CloseButtonIcon />
          </CloseButton>
        </Header>

        <Content>
          <DisplayList>
            {displayOptions.map((display) => (
              <DisplayOption
                key={display.id}
                selected={selectedDisplay === display.id}
              >
                <RadioButton onClick={() => handleDisplaySelect(display.id)}>
                  <RadioButtonInner selected={selectedDisplay === display.id} />
                </RadioButton>
                <DisplayInfo>
                  <DisplayTitle>{display.title}</DisplayTitle>
                  <DisplaySpecs>{display.specs}</DisplaySpecs>
                </DisplayInfo>
                <EditIconButton onClick={() => handleEditDisplay(display)}>
                  <EditIcon />
                </EditIconButton>
              </DisplayOption>
            ))}

            <AddSizeButton onClick={handleAddSizeClick}>
              <PlusIcon />
              사이즈 추가하기
            </AddSizeButton>
          </DisplayList>
        </Content>

        <Footer>
          <CompleteButton onClick={handleComplete} disabled={!selectedDisplay}>
            선택 완료
          </CompleteButton>
        </Footer>
      </PopupContent>
    </PopupContainer>
  );
};

export default DisplaySelectedPopup;

// Styled Components
const PopupContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  width: 880px;
  height: 641px;
  background: white;
  border-radius: 10px;
  border: 1px solid #e0e4eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 40px 40px 0 40px;
  flex-shrink: 0;
`;

const Title = styled.h2`
  color: #131416;
  font-size: 24px;
  font-family: Pretendard;
  font-weight: 700;
  line-height: 36px;
  margin: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: ${(props) =>
    props.isAddSizeMode ? "48px 40px 32px 40px" : "48px 40px 0 40px"};
  overflow-y: auto;
  flex: 1;
`;

const Footer = styled.div`
  padding: 32px 40px 40px 40px;
  flex-shrink: 0;
  border-top: 1px solid rgba(179, 183, 186, 0.2);
`;

const DisplayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DisplayOption = styled.div`
  width: 100%;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid
    ${(props) => (props.selected ? "#256EF4" : "rgba(179, 183, 186, 0.50)")};
  background: ${(props) =>
    props.selected ? "rgba(37, 110, 244, 0.05)" : "white"};
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
`;

const RadioButton = styled.div`
  width: 24px;
  height: 24px;
  padding: 2px;
  background: white;
  border-radius: 50%;
  border: 1px solid #58616a;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    border-color: #256ef4;
  }
`;

const RadioButtonInner = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => (props.selected ? "#256EF4" : "transparent")};
`;

const DisplayInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const DisplayTitle = styled.div`
  color: #131416;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 600;
  line-height: 28.5px;
`;

const DisplaySpecs = styled.div`
  color: #090f15;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
`;

const EditIcon = () => (
  <svg
    width="32"
    height="33"
    viewBox="0 0 32 33"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M21.9124 4.12883C22.329 3.71227 23.0044 3.71227 23.4209 4.12883L28.7542 9.46216C28.9543 9.6622 29.0667 9.93351 29.0667 10.2164C29.0667 10.4993 28.9543 10.7706 28.7542 10.9707L16.7542 22.9707C16.5542 23.1707 16.2829 23.2831 16 23.2831H10.6667C10.0776 23.2831 9.6 22.8055 9.6 22.2164V16.8831C9.6 16.6002 9.71238 16.3289 9.91242 16.1288L21.9124 4.12883ZM22.6667 6.39157L20.1752 8.88307L24 12.7079L26.4915 10.2164L22.6667 6.39157ZM22.4915 14.2164L18.6667 10.3916L11.7333 17.3249V21.1497H15.5582L22.4915 14.2164ZM6.4 7.01641C6.25272 7.01641 6.13333 7.1358 6.13333 7.28307V26.4831C6.13333 26.6303 6.25272 26.7497 6.4 26.7497H25.6C25.7473 26.7497 25.8667 26.6303 25.8667 26.4831V17.1497C25.8667 16.5606 26.3442 16.0831 26.9333 16.0831C27.5224 16.0831 28 16.5606 28 17.1497V26.4831C28 27.8086 26.9255 28.8831 25.6 28.8831H6.4C5.07452 28.8831 4 27.8086 4 26.4831V7.28307C4 5.95759 5.07452 4.88307 6.4 4.88307H15.7323C16.3214 4.88307 16.799 5.36064 16.799 5.94974C16.799 6.53884 16.3214 7.01641 15.7323 7.01641H6.4Z"
      fill="#8A949E"
    />
  </svg>
);

const AddSizeButton = styled.button`
  width: 100%;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #8a949e;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;

  &:hover {
    background: rgba(179, 183, 186, 0.15);
  }
`;

const PlusIcon = () => (
  <svg
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.99902 2.88281C10.3672 2.88281 10.666 3.18161 10.666 3.5498V9.7168H16.833C17.2011 9.7168 17.4999 10.0147 17.5 10.3828C17.5 10.751 17.2012 11.0498 16.833 11.0498H10.666V17.2158C10.666 17.584 10.3672 17.8828 9.99902 17.8828C9.63098 17.8826 9.33301 17.5839 9.33301 17.2158V11.0498H3.16699C2.7988 11.0498 2.5 10.751 2.5 10.3828C2.50009 10.0147 2.79886 9.7168 3.16699 9.7168H9.33301V3.5498C9.33301 3.18172 9.63098 2.88299 9.99902 2.88281Z"
      fill="#8A949E"
    />
  </svg>
);

const CompleteButton = styled.button`
  width: 100%;
  height: 48px;
  min-width: 78px;
  padding: 0 16px;
  background: ${(props) => (props.disabled ? "#ccc" : "#256EF4")};
  border-radius: 6px;
  border: none;
  color: white;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #1e5ad1;
  }
`;

// Add Size Form Styles
const AddDisplaySection = styled.div`
  padding: 20px;
  border-radius: 10px;
  border: 1px solid rgba(179, 183, 186, 0.5);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Label = styled.label`
  color: #464c53;
  font-size: 15px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 22.5px;
  text-align: left;
`;

const InputBox = styled.div`
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
`;

const InputText = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  color: #8a949e;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
  outline: none;

  &::placeholder {
    color: #8a949e;
  }
`;

const SelectBox = styled.div`
  width: 100%;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const SelectText = styled.span`
  color: #8a949e;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
`;

const ArrowIcon = ({ isOpen }) => (
  <svg
    width="24"
    height={isOpen ? "24" : "25"}
    viewBox={isOpen ? "0 0 24 24" : "0 0 24 25"}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {isOpen ? (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19.7344 15.5954C20.0628 15.2999 20.0894 14.7941 19.7939 14.4656L12.5947 6.46563C12.443 6.29705 12.2268 6.20078 12 6.20077C11.7732 6.20077 11.5571 6.29703 11.4054 6.4656L4.20537 14.4656C3.9098 14.794 3.93643 15.2998 4.26484 15.5954C4.59324 15.891 5.09908 15.8644 5.39464 15.5359L12 8.19669L18.6046 15.5359C18.9001 15.8643 19.4059 15.891 19.7344 15.5954Z"
        fill="#33363D"
      />
    ) : (
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M19.7344 8.78737C20.0628 9.08292 20.0894 9.58875 19.7939 9.91718L12.5947 17.9172C12.443 18.0858 12.2268 18.182 12 18.182C11.7732 18.182 11.5571 18.0858 11.4054 17.9172L4.20537 9.91721C3.9098 9.5888 3.93643 9.08297 4.26484 8.7874C4.59324 8.49184 5.09908 8.51846 5.39464 8.84687L12 16.1861L18.6046 8.8469C18.9001 8.51847 19.4059 8.49182 19.7344 8.78737Z"
        fill="#33363D"
      />
    )}
  </svg>
);

const InputRow = styled.div`
  display: flex;
  gap: 20px;
`;

const SizeInputRow = styled.div`
  display: flex;
  gap: 20px;
`;

const SizeBox = styled.div`
  flex: 1;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SizeText = styled.span`
  color: #8a949e;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
`;

const Unit = styled.span`
  color: #8a949e;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: ${(props) =>
    props.hasDelete ? "space-between" : "flex-end"};
  align-items: center;
  gap: 20px;
`;

const RightButtonGroup = styled.div`
  display: flex;
  gap: 20px;
`;

const EditIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const DeleteButton = styled.button`
  height: 48px;
  padding: 0 16px;
  background: rgba(255, 0, 0, 0.1);
  border-radius: 6px;
  border: none;
  color: #ff0000;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;

  &:hover {
    background: rgba(255, 0, 0, 0.15);
  }
`;

const TrashIcon = () => (
  <div
    style={{
      width: "24px",
      height: "24px",
      position: "relative",
      opacity: 0.1,
    }}
  >
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 6H5H21"
        stroke="#FF0000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
        stroke="#FF0000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 11V17"
        stroke="#FF0000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 11V17"
        stroke="#FF0000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const CancelButton = styled.button`
  width: 120px;
  height: 48px;
  min-width: 78px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  background: white;
  color: #131416;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

const SaveButton = styled.button`
  width: 200px;
  height: 48px;
  min-width: 78px;
  padding: 0 16px;
  background: black;
  border-radius: 6px;
  border: none;
  color: white;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
  cursor: pointer;

  &:hover {
    background: #333;
  }
`;

// CloseButtonIcon SVG 컴포넌트
const CloseButtonIcon = () => (
  <svg
    width="16"
    height="22"
    viewBox="0 0 16 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.6767 2.47245C16.1079 2.02132 16.1079 1.28988 15.6767 0.838745C15.2455 0.387609 14.5464 0.387609 14.1152 0.838745L7.99942 7.23754L1.8846 0.839765C1.45341 0.388629 0.754327 0.388629 0.323143 0.839765C-0.10804 1.2909 -0.10804 2.02234 0.323144 2.47347L6.43797 8.87124L1.03279 14.5265C0.601602 14.9777 0.601602 15.7091 1.03279 16.1602C1.46397 16.6114 2.16306 16.6114 2.59424 16.1602L7.99943 10.505L13.4056 16.1613C13.8368 16.6124 14.5359 16.6124 14.967 16.1613C15.3982 15.7101 15.3982 14.9787 14.967 14.5276L9.56088 8.87124L15.6767 2.47245Z"
      fill="#666666"
    />
  </svg>
);

// CloseButton 스타일
const CloseButton = styled.div`
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

// Resolution Dropdown Styles
const ResolutionSelectContainer = styled.div`
  position: relative;
`;

const ResolutionDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  border: 1px solid #cdd1d5;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
  padding: 8px;
  margin-top: 4px;
`;

const ResolutionOption = styled.div`
  height: 48px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: rgba(179, 183, 186, 0.1);
  }
`;

const ResolutionCheckbox = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  background: white;
  border-radius: 4px;
  border: 1px solid #bebebe;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const ResolutionLabel = styled.span`
  color: #1e2124;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
`;

const CustomResolutionInput = styled.input`
  width: 100%;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  border: none;
  color: #1e2124;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
  outline: none;

  &::placeholder {
    color: #8a949e;
  }

  &:focus {
    background: rgba(179, 183, 186, 0.1);
  }
`;

// Inch Dropdown Styles
const InchSelectContainer = styled.div`
  position: relative;
`;

const InchDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  border: 1px solid #cdd1d5;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
  padding: 8px;
  margin-top: 4px;
`;

const InchOption = styled.div`
  height: 48px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: rgba(179, 183, 186, 0.1);
  }
`;

const InchCheckbox = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  background: white;
  border-radius: 4px;
  border: 1px solid #bebebe;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const InchLabel = styled.span`
  color: #1e2124;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 25.5px;
`;

const CustomInchInput = styled.input`
  width: 100%;
  height: 56px;
  padding: 0 16px;
  background: rgba(179, 183, 186, 0.1);
  border-radius: 8px;
  border: none;
  color: #1e2124;
  font-size: 19px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 28.5px;
  outline: none;

  &::placeholder {
    color: #8a949e;
  }

  &:focus {
    background: rgba(179, 183, 186, 0.1);
  }
`;
